import React, { useState, useEffect } from 'react';
import {
  Boxes,
  Search,
  AlertTriangle,
  Plus,
  Minus,
  Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { inventoryApi, productsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Inventory = () => {
  const { isAdmin, isWarehouse } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [adjustModal, setAdjustModal] = useState(null);
  
  const [adjustmentData, setAdjustmentData] = useState({
    adjustment: '',
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, [showLowStockOnly]);

  const fetchData = async () => {
    try {
      const [invRes, prodRes] = await Promise.all([
        inventoryApi.getAll(showLowStockOnly),
        productsApi.getAll({ active_only: false })
      ]);
      setInventory(invRes.data || []);
      setProducts(prodRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjust = async () => {
    if (!adjustmentData.adjustment || !adjustmentData.reason) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await inventoryApi.adjust({
        product_id: adjustModal.product_id,
        adjustment: parseInt(adjustmentData.adjustment),
        reason: adjustmentData.reason
      });
      toast.success('Inventory adjusted successfully');
      setAdjustModal(null);
      setAdjustmentData({ adjustment: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const lowStockCount = inventory.filter(item => item.needs_reorder).length;

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.cas_number && item.cas_number.includes(searchTerm));
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="inventory-loading">
        <div className="skeleton h-12 w-48 rounded-md"></div>
        <div className="skeleton h-96 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventory</h1>
          <p className="text-slate-600 mt-1">{inventory.length} products in stock</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stats-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Products</p>
                <p className="text-2xl font-bold text-slate-900">{inventory.length}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-md">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card border-amber-200 bg-amber-50" data-testid="low-stock-stat">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Low Stock Items</p>
                <p className="text-2xl font-bold text-amber-800">{lowStockCount}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-md">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Stock Items</p>
                <p className="text-2xl font-bold text-slate-900">{inventory.length - lowStockCount}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-md">
                <Boxes className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by product name or CAS number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="inventory-search"
              />
            </div>
            <Button
              variant={showLowStockOnly ? 'default' : 'outline'}
              onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              className={showLowStockOnly ? 'bg-amber-600 hover:bg-amber-700' : ''}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Low Stock Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        <CardContent className="p-0">
          {filteredInventory.length === 0 ? (
            <div className="empty-state py-16">
              <Boxes className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No inventory items found</h3>
              <p className="text-slate-500">Try adjusting your search filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>CAS Number</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right">Reorder Level</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Updated</TableHead>
                    {(isAdmin || isWarehouse) && (
                      <TableHead className="text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInventory.map((item) => (
                    <TableRow key={item.id} data-testid={`inventory-row-${item.id}`}>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="font-mono text-sm">{item.cas_number || '-'}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono ${item.needs_reorder ? 'text-red-600 font-bold' : ''}`}>
                          {item.stock_quantity} {item.unit}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">{item.reorder_level} {item.unit}</TableCell>
                      <TableCell>
                        {item.needs_reorder ? (
                          <Badge className="bg-red-50 text-red-700 border border-red-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Low Stock
                          </Badge>
                        ) : (
                          <Badge className="bg-green-50 text-green-700 border border-green-200">
                            In Stock
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {item.last_updated ? new Date(item.last_updated).toLocaleDateString() : '-'}
                      </TableCell>
                      {(isAdmin || isWarehouse) && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8"
                              onClick={() => setAdjustModal(item)}
                              data-testid={`adjust-btn-${item.id}`}
                            >
                              Adjust
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjust Inventory Modal */}
      <Dialog open={!!adjustModal} onOpenChange={() => setAdjustModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Inventory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-md p-3">
              <p className="font-medium">{adjustModal?.product_name}</p>
              <p className="text-sm text-slate-500">
                Current stock: <span className="font-mono">{adjustModal?.stock_quantity} {adjustModal?.unit}</span>
              </p>
            </div>

            <div>
              <Label className="form-label">Adjustment (+/-)</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setAdjustmentData({ 
                    ...adjustmentData, 
                    adjustment: String(parseInt(adjustmentData.adjustment || 0) - 1) 
                  })}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  value={adjustmentData.adjustment}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, adjustment: e.target.value })}
                  placeholder="0"
                  className="text-center font-mono"
                  data-testid="adjustment-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setAdjustmentData({ 
                    ...adjustmentData, 
                    adjustment: String(parseInt(adjustmentData.adjustment || 0) + 1) 
                  })}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Use positive numbers to add stock, negative to remove.
              </p>
            </div>

            <div>
              <Label className="form-label">Reason</Label>
              <Select 
                value={adjustmentData.reason} 
                onValueChange={(value) => setAdjustmentData({ ...adjustmentData, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="received_shipment">Received Shipment</SelectItem>
                  <SelectItem value="inventory_count">Inventory Count Correction</SelectItem>
                  <SelectItem value="damaged">Damaged/Spoiled</SelectItem>
                  <SelectItem value="returned">Customer Return</SelectItem>
                  <SelectItem value="sample">Sample/Testing</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAdjustModal(null)}>
              Cancel
            </Button>
            <Button onClick={handleAdjust} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-adjustment-button">
              Apply Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
